import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, MagnifyingGlass, Sparkle } from "@phosphor-icons/react";
import HseQuestionField from "./HseQuestionField";

/** Step 1 pick a document type (grouped), step 2 answer its short guided questions → AI drafts it in the background. */
export default function HseNewDocumentDialog({ open, onOpenChange, catalogue, presetType = "" }) {
  const navigate = useNavigate();
  const [typeKey, setTypeKey] = useState(presetType);
  const [answers, setAnswers] = useState({});
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setTypeKey(presetType); setAnswers({}); setSearch(""); setGroup(""); } }, [open, presetType]);

  const types = useMemo(() => catalogue?.types || [], [catalogue]);
  const type = types.find(t => t.key === typeKey);
  const visible = useMemo(() => types.filter(t => (!group || t.group === group) && (!search || `${t.label} ${t.blurb}`.toLowerCase().includes(search.toLowerCase()))), [types, group, search]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/hse/documents", { type_key: typeKey, answers });
      toast.success(`${data.reference} — the AI is writing your ${type.label}`);
      onOpenChange(false);
      navigate(`/health-safety/docs/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hse-new-doc-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo] flex items-center gap-2">
            {type && <button type="button" onClick={() => setTypeKey("")} className="text-muted-foreground hover:text-foreground" data-testid="hse-new-doc-back"><ArrowLeft size={16} /></button>}
            {type ? type.label : "New HSE document"}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">{type ? type.blurb : "Pick what you need — the AI writes a complete, ready-to-review draft from your company profile and a few answers."}</p>
        </DialogHeader>

        {!type && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search documents…" className="h-8 pl-8 text-sm" data-testid="hse-new-doc-search" />
              </div>
              <div className="flex flex-wrap gap-1">
                <button type="button" onClick={() => setGroup("")} className={`text-[11px] px-2 py-1 rounded-md border ${!group ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`} data-testid="hse-new-doc-group-all">All</button>
                {(catalogue?.groups || []).map(g => (
                  <button key={g.key} type="button" onClick={() => setGroup(g.key)} className={`text-[11px] px-2 py-1 rounded-md border ${group === g.key ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`} data-testid={`hse-new-doc-group-${g.key}`}>{g.label}</button>
                ))}
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {visible.map(t => {
                const c = catalogue?.counts?.[t.key];
                return (
                  <button key={t.key} type="button" onClick={() => setTypeKey(t.key)} className="text-left border border-border rounded-md p-3 hover:border-primary/60 hover:bg-muted/40 transition-colors" data-testid={`hse-new-doc-type-${t.key}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-semibold leading-tight">{t.label}</span>
                      {c?.approved ? <Badge className="bg-emerald-100 text-emerald-700 text-[9px] shrink-0">{c.approved} live</Badge> : c?.draft ? <Badge className="bg-slate-100 text-slate-700 text-[9px] shrink-0">draft</Badge> : null}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{t.blurb}</p>
                    <p className="text-[10px] text-muted-foreground/80 mt-1">{t.legal}</p>
                  </button>
                );
              })}
              {!visible.length && <p className="text-sm text-muted-foreground col-span-2 py-6 text-center">No document types match.</p>}
            </div>
          </div>
        )}

        {type && (
          <>
            <div className="grid gap-4 sm:grid-cols-2" data-testid="hse-new-doc-questions">
              {type.questions.map(qd => (
                <div key={qd.key} className={qd.type === "textarea" || qd.type === "multiselect" ? "sm:col-span-2" : ""}>
                  <HseQuestionField q={qd} value={answers[qd.key]} onChange={v => setAnswers(a => ({ ...a, [qd.key]: v }))} />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="hse-new-doc-cancel">Cancel</Button>
              <Button onClick={create} disabled={busy} data-testid="hse-new-doc-create"><Sparkle size={14} className="mr-1" />{busy ? "Starting…" : "Draft with AI"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
